import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { backupState } from './backups';

/**
 * The red bar that appears when the last good backup is too old.
 *
 * This is the whole reason `backup_runs` exists. A backup system with no visible
 * signal of failure is not a backup system — it goes on saying nothing until the
 * day somebody needs the data, and by then the silence has been the answer for
 * months. So it sits above every screen, not on a page one would have to think
 * to visit, and it is shown only to the people who can do something about it.
 */
export function BackupBanner({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const state = useQuery({
    queryKey: ['backups', 'banner'],
    queryFn: () => backupState(1),
    // Once every quarter of an hour. The condition changes on the scale of
    // days; polling harder would only add requests.
    refetchInterval: 15 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const health = state.data?.health;
  if (!health?.stale) return null;

  return (
    <div className="shell__alarm" role="alert">
      <span>
        {health.last_success_at
          ? t('backups.bannerStale', { hours: health.stale_after_hours })
          : t('backups.bannerNever')}
      </span>
      <button type="button" onClick={onOpen}>
        {t('backups.open')}
      </button>
    </div>
  );
}
